import type { FetchArgs } from '@reduxjs/toolkit/query';

/** Most asset ids the BFF accepts in one tags request. */
export const MAX_IPR_IDS_PER_REQUEST = 5000;

/**
 * Asset ids per read request (`/assets/basic-information`, `/assets/tags/by-ids`). The BFF sends
 * each request to the facade as parallel batches of 100, and the facade fails above about ten.
 */
export const READ_IPR_IDS_PER_REQUEST = 500;

export const chunkIprIds = (iprIds: string[], size = MAX_IPR_IDS_PER_REQUEST): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < iprIds.length; i += size) {
    chunks.push(iprIds.slice(i, i + size));
  }
  return chunks;
};

type ChunkResponse<Error> = { data?: unknown; error?: Error };

interface ChunkOptions<Result> {
  chunkSize?: number;
  /** Builds the result for ids not applied after a later chunk fails, so earlier chunks still report their outcome. */
  failedIprIds?: (iprIds: string[]) => Result;
}

/**
 * Posts `iprIds` to `url` in requests of at most `chunkSize` ids, one after another, and merges the
 * responses. Stops at the first failed request: without `failedIprIds`, or when the first request
 * fails, it returns that error; otherwise the ids not applied are merged in as `failedIprIds`.
 */
export const postInIprIdChunks = async <Result, Error>(
  baseQuery: (arg: FetchArgs) => PromiseLike<ChunkResponse<Error>> | ChunkResponse<Error>,
  url: string,
  iprIds: string[],
  body: (chunk: string[]) => object,
  merge: (results: Result[]) => Result,
  { chunkSize = MAX_IPR_IDS_PER_REQUEST, failedIprIds }: ChunkOptions<Result> = {}
): Promise<{ data: Result } | { error: Error }> => {
  const results: Result[] = [];
  const chunks = chunkIprIds(iprIds, chunkSize);
  for (const [index, chunk] of chunks.entries()) {
    const response = await baseQuery({ url, method: 'POST', body: body(chunk) });
    if (response.error !== undefined) {
      if (!failedIprIds || index === 0) {
        return { error: response.error };
      }
      results.push(failedIprIds(chunks.slice(index).flat()));
      return { data: merge(results) };
    }
    results.push(response.data as Result);
  }
  return { data: merge(results) };
};
